/**
 * Reads a backup made by the Medicine Purchase Calculator (any version that
 * exports .json) and turns it into the app's storage keys, so a new family
 * can start with all existing data.
 */
import { DEVICE_KEYS, PEOPLE_KEY, isAppKey } from '@bhorosha/data';

export interface ParsedBackup {
  keys: Record<string, string>;
  summary: { people: number; medicines: number; reminders: number; visits: number; tests: number };
  activePersonId?: string;
}

const LEGACY_TOP_LEVEL = new Set(['mpc_medicines_v1', 'mpc_library_v1', 'mpc_settings_v1']);

export function parseBackup(text: string): ParsedBackup | null {
  let data: any;
  try { data = JSON.parse(String(text).replace(/^﻿/, '').trim()); } catch { return null; }
  if (!data || typeof data !== 'object') return null;
  const keys: Record<string, string> = {};

  if (data.all_storage && typeof data.all_storage === 'object' && Object.keys(data.all_storage).length) {
    for (const [k, v] of Object.entries(data.all_storage)) {
      if (typeof v === 'string' && isAppKey(k)) keys[k] = v;
    }
  } else if (Array.isArray(data.people) && Array.isArray(data.data)) {
    keys[PEOPLE_KEY] = JSON.stringify(data.people);
    for (const e of data.data) {
      const pid = e.person_id;
      const put = (prefix: string, arr: unknown) => { if (Array.isArray(arr)) keys[`${prefix}::${pid}`] = JSON.stringify(arr); };
      put('mpc_medicines_v1', e.medicines); put('mpc_library_v1', e.library); put('mpc_reminders_v1', e.reminders);
      put('mpc_visits_v1', e.visits); put('mpc_doctors_v1', e.doctors); put('mpc_tests_v1', e.tests); put('mpc_vaccines_v1', e.vaccines);
    }
    if (Array.isArray(data.test_library)) keys.mpc_testlib_v1 = JSON.stringify(data.test_library);
    if (Array.isArray(data.medicine_types) && data.medicine_types.length) keys.mpc_types_v1 = JSON.stringify(data.medicine_types);
    if (data.active_person_id) keys.mpc_active_person_v1 = String(data.active_person_id);
  } else if (Array.isArray(data.medicines)) {
    // Very old single-person backup: the app's own migration turns these into "Patient 1".
    keys.mpc_medicines_v1 = JSON.stringify(data.medicines);
    if (Array.isArray(data.library)) keys.mpc_library_v1 = JSON.stringify(data.library);
  } else {
    return null;
  }

  // Once people exist, the pre-multi-person keys are unused leftovers.
  if (keys[PEOPLE_KEY]) for (const k of LEGACY_TOP_LEVEL) delete keys[k];
  // Notification permission belongs to the device, not the backup.
  delete keys.mpc_notif_perm_v1;

  const count = (prefix: string) => Object.entries(keys).filter(([k]) => k.startsWith(prefix + '::'))
    .reduce((n, [, v]) => { try { const a = JSON.parse(v); return n + (Array.isArray(a) ? a.length : 0); } catch { return n; } }, 0);
  let people = 0;
  try { people = keys[PEOPLE_KEY] ? JSON.parse(keys[PEOPLE_KEY]).length : (keys.mpc_medicines_v1 ? 1 : 0); } catch { /* ignore */ }
  return {
    keys,
    activePersonId: keys.mpc_active_person_v1,
    summary: {
      people, medicines: count('mpc_medicines_v1') || (keys.mpc_medicines_v1 ? JSON.parse(keys.mpc_medicines_v1).length : 0),
      reminders: count('mpc_reminders_v1'), visits: count('mpc_visits_v1'), tests: count('mpc_tests_v1'),
    },
  };
}

/** Write a parsed backup into a store (synced keys are sent to the server; device keys stay local). */
export function writeBackup(store: { setItem(k: string, v: string): void }, b: ParsedBackup) {
  const order = (k: string) => (k === PEOPLE_KEY ? 0 : DEVICE_KEYS.has(k) ? 2 : 1);
  Object.keys(b.keys).sort((a, c) => order(a) - order(c)).forEach(k => store.setItem(k, b.keys[k]));
}
